/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import { Box, Typography } from '@mui/material';
import { lookupEventColor } from './IPAWSLayer';

const borderColor = (event?: string) => {
  if (event == null) {
    return '#000';
  }
  // An event the palette does not cover (NWS publishes more event values than
  // it has keys) reads back undefined, and so does a prototype name like
  // `constructor`; both fall back to the same border as a missing event
  // instead of throwing on .join inside the popup's render.
  const color = lookupEventColor(event);
  if (color == null) {
    return '#000';
  }
  return `rgb(${color.join(',')})`;
};

type IPAWSAlertInfoProps = Partial<{
  response: string;
  urgency: string;
  certainty: string;
  severity: string;
  description: string | undefined | null;
  event: string;
  sender: string;
}>;

export const IPAWSAlertInfo = (props: IPAWSAlertInfoProps) => {
  const { response, urgency, certainty, severity, description, event, sender } = props;

  return (
    <Box
      sx={{
        pl: 2,
        pr: 0,
        minWidth: 354,
        maxWidth: 400,
        borderLeft: `4px solid ${borderColor(event)}`,
      }}
    >
      <Typography variant="caption">{sender}</Typography>

      <Typography variant="h6">
        {severity} {event}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          textTransform: 'uppercase',
        }}
      >
        {response} - {urgency} & {certainty}
      </Typography>

      <Box sx={{ mt: 1, pr: 2 }}>
        {description?.split('  ').map((word, index) => (
          <Typography variant="body2" key={index}>
            {word}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};
